import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { baseURL, getTrash } from "../redux/Action";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import usePagination from "../Hooks/usePagination";
import { Pagination } from "@mui/material";
import { Navigate } from "react-router-dom";
import parse from "html-react-parser";
import { months } from "../Public/constants";
import { RiDeleteBinLine, RiRefreshLine } from "react-icons/ri";

const TrashBlogs = () => {
  const { isLoading, isError, trash, isAuth, trashCount, token } = useSelector(
    (state) => state,
  );

  const dispatch = useDispatch();

  let [page, setPage] = useState(1);
  const PER_PAGE = 4;

  const count = Math.ceil(trash.length / PER_PAGE);
  const { jump, currentData } = usePagination(trash, PER_PAGE);

  const handleChange = (e, p) => {
    setPage(p);
    jump(p);
  };

  useEffect(() => {
    document.title = "Blogshog | Blogs in Trash";
    getTrash(dispatch);
  }, [dispatch]);

  if (!isAuth) {
    return <Navigate to={"/login"} />;
  }

  if (trashCount === 0 && !isLoading) {
    return (
      <section className="min-h-[70vh] mt-16 flex flex-col items-center justify-center font-sans text-black dark:text-white">
        <div className="w-32 h-32 border-4 border-black dark:border-white/30 bg-[#f4f4f4] dark:bg-black flex items-center justify-center mb-8 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] dark:shadow-[8px_8px_0px_0px_rgba(255,255,255,0.5)]">
          <RiDeleteBinLine className="text-6xl text-black dark:text-white" />
        </div>
        <h2 className="text-4xl sm:text-6xl font-black text-black dark:text-white uppercase tracking-tighter mb-4 text-center">
          RECYCLE BIN EMPTY
        </h2>
        <p className="text-black dark:text-white font-mono font-bold uppercase tracking-widest bg-yellow-300 dark:bg-yellow-600 px-4 py-2 border-2 border-black dark:border-white/30 border-dashed text-center">
          NO RECORDS FOUND IN TRASH REPOSITORY.
        </p>
      </section>
    );
  }

  const handleRestore = (id) => {
    const toastId = toast.loading("RESTORING RECORD...", { theme: "light" });

    axios
      .delete(`${baseURL}/trash/${id}/restore`, {
        withCredentials: true,
        headers: {
          Authorization: token,
        },
      })
      .then((res) => {
        toast.update(toastId, {
          render: "RECORD RESTORED",
          type: "success",
          isLoading: false,
          theme: "light",
          autoClose: 2000,
          closeOnClick: true,
        });
        if (res.status === 200) {
          setTimeout(() => {
            getTrash(dispatch);
          }, 2000);
        }
      })
      .catch((err) => {
        toast.update(toastId, {
          render: "RESTORE FAILED",
          type: "error",
          isLoading: false,
          theme: "light",
          autoClose: 2000,
          closeOnClick: true,
        });
      });
  };

  const handleDelete = (id) => {
    const toastId = toast.loading("PURGING RECORD...", { theme: "light" });

    axios
      .delete(`${baseURL}/trash/${id}`, {
        withCredentials: true,
        headers: {
          Authorization: token,
        },
      })
      .then((res) => {
        toast.update(toastId, {
          render: "RECORD PURGED",
          type: "success",
          isLoading: false,
          theme: "light",
          autoClose: 2000,
          closeOnClick: true,
        });
        if (res.status === 200) {
          setTimeout(() => {
            getTrash(dispatch);
          }, 2000);
        }
      })
      .catch((err) => {
        toast.update(toastId, {
          render: "PURGE FAILED",
          type: "error",
          isLoading: false,
          theme: "light",
          autoClose: 2000,
          closeOnClick: true,
        });
      });
  };

  return (
    <div className="min-h-screen py-24 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto font-sans text-black dark:text-white mt-[60px]">
      <div className="mb-16 border-b-4 border-black dark:border-white/30 pb-8">
        <h1 className="text-6xl sm:text-7xl font-black text-black dark:text-white tracking-tighter uppercase leading-none mb-4">
          TRASH <br /> REPOSITORY
        </h1>
      </div>

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 10 }}>
          <CircularProgress color="inherit" thickness={5} size={60} />
        </Box>
      ) : isError ? (
        <div className="flex justify-center mt-10">
          <div className="bg-red-300 dark:bg-red-900 border-2 border-black dark:border-white/30 text-black dark:text-white font-mono font-bold uppercase tracking-widest px-8 py-4 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] dark:shadow-[4px_4px_0px_0px_rgba(255,255,255,0.5)]">
            FATAL ERROR ACCESSING TRASH REPOSITORY.
          </div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
            {currentData().map((el) => {
              const dateObj = new Date(el.createdAt);
              const date = dateObj.getDate();
              const month = months[dateObj.getMonth()];
              return (
                <div
                  key={el._id}
                  className="bg-white dark:bg-black border-4 border-black dark:border-white/30 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] dark:shadow-[8px_8px_0px_0px_rgba(255,255,255,0.5)] flex flex-col sm:flex-row relative group text-black dark:text-white"
                >
                  <div className="absolute top-0 right-0 bg-black dark:bg-white text-white dark:text-black px-2 py-1 text-[8px] font-mono tracking-widest border-l border-b border-black dark:border-white/30 uppercase z-10 font-bold shadow-[-2px_2px_0px_0px_rgba(0,0,0,1)] dark:shadow-[-2px_2px_0px_0px_rgba(255,255,255,0.5)]">
                    INDEX: {el._id.slice(-6)}
                  </div>

                  <div className="w-24 sm:w-32 flex-shrink-0 flex flex-col justify-center items-center text-center bg-black dark:bg-[#111] py-6 sm:py-8 border-r-4 border-black dark:border-white/30 self-stretch">
                    <span className="text-white dark:text-gray-300 font-mono font-bold text-[10px] tracking-widest uppercase mb-2 border-b border-white/30 dark:border-gray-600/50 pb-1">
                      {month}
                    </span>
                    <span className="font-black text-4xl sm:text-5xl text-white dark:text-gray-100 leading-none">
                      {date}
                    </span>
                  </div>

                  <div className="flex-grow flex flex-col p-6 sm:p-8">
                    <h2 className="text-2xl font-black text-black dark:text-white uppercase mb-4 leading-none line-clamp-2 underline decoration-4 decoration-transparent group-hover:decoration-black dark:group-hover:decoration-white transition-all underline-offset-4">
                      {el.title}
                    </h2>

                    <div className="text-black dark:text-gray-300 font-mono text-xs uppercase font-bold text-ellipsis overflow-hidden h-[3em] line-clamp-2 mb-8 border-l-4 border-black dark:border-white/30 pl-4">
                      {parse(el.body)}
                    </div>

                    <div className="mt-auto flex flex-wrap items-center gap-4">
                      <button
                        className="flex-1 flex items-center justify-center gap-2 text-[10px] font-black uppercase tracking-widest text-black dark:text-white bg-[#f4f4f4] dark:bg-black hover:bg-black hover:text-white dark:hover:bg-white dark:hover:text-black border-2 border-black dark:border-white/30 px-4 py-3 shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] dark:shadow-[2px_2px_0px_0px_rgba(255,255,255,0.5)] active:shadow-none dark:active:shadow-none active:translate-x-0.5 active:translate-y-0.5 transition-all"
                        onClick={() => handleRestore(el._id)}
                      >
                        <RiRefreshLine className="text-lg" /> ACQUIRE
                      </button>
                      <button
                        className="flex-1 flex items-center justify-center gap-2 text-[10px] font-black uppercase tracking-widest text-white dark:text-black bg-red-600 dark:bg-red-500 hover:bg-black hover:text-white dark:hover:bg-white dark:hover:text-black border-2 border-black dark:border-white/30 px-4 py-3 shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] dark:shadow-[2px_2px_0px_0px_rgba(255,255,255,0.5)] active:shadow-none dark:active:shadow-none active:translate-x-0.5 active:translate-y-0.5 transition-all"
                        onClick={() => handleDelete(el._id)}
                      >
                        <RiDeleteBinLine className="text-lg" /> PURGE
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {trashCount > PER_PAGE && (
            <div className="flex justify-center mt-16 mb-24 global-pagination-wrapper">
              <Pagination
                count={count}
                variant="outlined"
                shape="rounded"
                page={page}
                onChange={handleChange}
                sx={{
                  "& .MuiPaginationItem-root": {
                    color: "inherit",
                    borderColor: "currentColor",
                    borderWidth: "2px",
                    fontFamily: "monospace",
                    fontWeight: "bold",
                    fontSize: "1rem",
                    borderRadius: "0",
                    marginX: "4px",
                  },
                  "& .MuiPaginationItem-root:hover": {
                    backgroundColor: "rgba(128,128,128,0.2)",
                  },
                  "& .Mui-selected": {
                    backgroundColor: "currentColor !important",
                    color: "var(--bg-color, white) !important", // Using CSS variable or fallback for text color
                    boxShadow: "4px 4px 0px 0px currentColor",
                  },
                }}
                className="dark:[&_.Mui-selected]:!text-black"
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TrashBlogs;
